// A screen pushed over the parent tabs (Edit details, Add a child, an address, a child, Cart).
// Phone chrome: the top app bar (up arrow + title), the content scrolling full width, and the
// screen's main action pinned to the bottom edge, riding above the keyboard, the way Android apps
// place Save. Desktop chrome keeps the page form: the school header above, a display title, the
// content in a centred column and Cancel + the action at the end.
import { type ReactNode, useEffect, useState } from "react";
import {
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { makeStyles } from "@/theme/makeStyles";
import { Text } from "@/ui/Text";

import { AppBar, type AppBarProps } from "./chrome/AppBar";
import { useChromeKind } from "./chrome/useChromeKind";

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    screen: { flex: 1, backgroundColor: t.color.background },
    fill: { flex: 1 },
    // Phone: full width with the 16 px gutters; nothing centred, nothing capped.
    content: {
      flexGrow: 1,
      paddingHorizontal: t.space.lg,
      paddingTop: t.space.xs,
      gap: t.space.lg,
    },
    actionBar: {
      paddingHorizontal: t.space.lg,
      paddingTop: t.space.md,
      gap: t.space.sm,
      backgroundColor: t.color.background,
      borderTopWidth: t.borderWidth.card,
      borderTopColor: t.color.border,
    },
    // Desktop: the page form it always was.
    page: {
      flexGrow: 1,
      alignItems: "center",
      paddingHorizontal: t.space.lg,
      paddingVertical: t.space.x2l,
    },
    column: { width: "100%", maxWidth: 1080, gap: t.space.lg },
    narrow: { width: "100%", maxWidth: 560, gap: t.space.lg },
    buttons: { flexDirection: "row", gap: t.space.sm, flexWrap: "wrap" },
  }),
);

/** True while the software keyboard is up (native only). */
function useKeyboardShown(): boolean {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (Platform.OS === "web") return;
    const show = Keyboard.addListener("keyboardDidShow", () => setShown(true));
    const hide = Keyboard.addListener("keyboardDidHide", () => setShown(false));
    return () => {
      show.remove();
      hide.remove();
    };
  }, []);
  return shown;
}

export type ScreenScaffoldProps = Pick<
  AppBarProps,
  "onBack" | "fallbackHref" | "end"
> & {
  /** The app bar title on phones; the page heading on the desktop. */
  title: string;
  /** A muted line under the title. */
  subtitle?: string;
  testID: string;
  /** Desktop: the 560 px form column instead of the 1080 px page column. */
  narrow?: boolean;
  /** The screen's main action (e.g. Save): pinned to the bottom on phones. */
  action?: ReactNode;
  /** Desktop only: Cancel beside the action. Phones go back with the arrow or Android Back. */
  cancel?: ReactNode;
  children: ReactNode;
};

export function ScreenScaffold({
  title,
  subtitle,
  testID,
  narrow = false,
  action,
  cancel,
  onBack,
  fallbackHref,
  end,
  children,
}: ScreenScaffoldProps) {
  const styles = useStyles();
  const chrome = useChromeKind();
  const insets = useSafeAreaInsets();
  const keyboard = useKeyboardShown();

  if (chrome === "desktop") {
    return (
      <View style={styles.screen} testID={testID}>
        <ScrollView
          contentContainerStyle={styles.page}
          keyboardShouldPersistTaps="handled"
          role="main"
        >
          <View style={narrow ? styles.narrow : styles.column}>
            <Text variant="display" accessibilityRole="header" aria-level={1}>
              {title}
            </Text>
            {subtitle ? <Text tone="muted">{subtitle}</Text> : null}
            {children}
            {action || cancel ? (
              <View style={styles.buttons}>
                {cancel}
                {action}
              </View>
            ) : null}
          </View>
        </ScrollView>
      </View>
    );
  }

  // With the keyboard up the action bar sits on the keyboard; otherwise above the gesture bar.
  const bottom = keyboard ? 0 : insets.bottom;
  return (
    <View style={styles.screen} testID={testID}>
      <AppBar
        title={title}
        onBack={onBack}
        fallbackHref={fallbackHref}
        end={end}
      />
      <KeyboardAvoidingView
        style={styles.fill}
        behavior={Platform.OS === "web" ? undefined : "padding"}
      >
        <ScrollView
          style={styles.fill}
          contentContainerStyle={[
            styles.content,
            { paddingBottom: (action ? 24 : 32) + (action ? 0 : bottom) },
          ]}
          keyboardShouldPersistTaps="handled"
          role="main"
        >
          {subtitle ? <Text tone="muted">{subtitle}</Text> : null}
          {children}
        </ScrollView>
        {action ? (
          <View
            style={[styles.actionBar, { paddingBottom: Math.max(bottom, 12) }]}
            testID={`${testID}-action-bar`}
          >
            {action}
          </View>
        ) : null}
      </KeyboardAvoidingView>
    </View>
  );
}
